import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  loadReceivedRequests,
  approveBorrowRequest,
  denyBorrowRequest,
  refreshAllRequests,
  setCurrentFilter,
  setActiveTab,
  setSelectedRequest,
  clearError,
  clearManagementError,
  selectReceivedRequests,
  selectSelectedRequest,
  selectCurrentFilter,
  selectActiveTab,
  selectBorrowRequestsLoading,
  selectBorrowRequestsError,
  selectManagementError,
  selectPendingCount,
  selectTotalReceived,
  selectFilteredReceivedRequests,
  selectRequestsExpiringSoon,
  loadSentRequests,
  cancelSentRequest,
  selectSentRequests,
  claimSentRequest,
} from './borrowRequestsSlice';
import { PrimaryCTA } from '../../components';
import { SmartImage } from '../../components/SmartImage';
import { fpColors } from '../../styles/theme';
import Toast from 'react-native-toast-message';
import { CrewContextChips } from './CrewContextChips';
import { api } from '../../api/remembrApi';
import type {
  BorrowRequestDetails,
  BorrowRequestsQuery,
} from '../../api/borrowTypes';

type RequestStatus = BorrowRequestDetails['status'];

/**
 * Borrow requests — Fidelity Purple restyle.
 *
 * Visual pass only; every existing thunk and state interaction is
 * preserved verbatim (loadReceivedRequests, approveBorrowRequest,
 * denyBorrowRequest, cancelSentRequest, claimSentRequest, filter +
 * tab state). Rendered inside SharingRequestsScreen which already
 * provides the page-level top bar.
 */
export const BorrowRequestsManager: React.FC = () => {
  const dispatch = useAppDispatch();

  const receivedRequests = useAppSelector(selectReceivedRequests);
  const sentRequests = useAppSelector(selectSentRequests);
  const filteredRequests = useAppSelector(selectFilteredReceivedRequests);
  const selectedRequest = useAppSelector(selectSelectedRequest);
  const currentFilter = useAppSelector(selectCurrentFilter);
  const activeTab = useAppSelector(selectActiveTab);
  const loading = useAppSelector(selectBorrowRequestsLoading);
  const error = useAppSelector(selectBorrowRequestsError);
  const managementError = useAppSelector(selectManagementError);
  const pendingCount = useAppSelector(selectPendingCount);
  const totalReceived = useAppSelector(selectTotalReceived);
  const expiringSoon = useAppSelector(selectRequestsExpiringSoon);

  const [refreshing, setRefreshing] = useState(false);
  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const [responseAction, setResponseAction] =
    useState<'APPROVE' | 'DENY' | null>(null);
  const [responseMessage, setResponseMessage] = useState('');

  useEffect(() => {
    dispatch(loadReceivedRequests());
    dispatch(loadSentRequests());
  }, [dispatch]);

  // FE-253 — honor `initialTab` route param on first mount so notification
  // deep-links land users on the right tab (sender-side notifications
  // open 'sent', owner-side open 'received'). Read once: if the user
  // later flips the segmented control manually, we don't snap them
  // back. No-op when the screen was opened without the param.
  const route = useRoute<any>();
  useEffect(() => {
    const initialTab = (route.params as any)?.initialTab as
      | 'sent'
      | 'received'
      | undefined;
    if (initialTab === 'sent' || initialTab === 'received') {
      dispatch(setActiveTab(initialTab));
    }
    // Read once on mount only — intentionally omitting `route.params`
    // from deps so re-orderings don't reset the tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      Toast.show({
        type: 'error',
        text1: 'Unable to load requests',
        text2: error,
      });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (managementError) {
      Toast.show({
        type: 'error',
        text1: 'Action failed',
        text2: managementError,
      });
      dispatch(clearManagementError());
    }
  }, [managementError, dispatch]);

  const handleRetry = useCallback(() => {
    dispatch(clearError());
    dispatch(loadReceivedRequests());
  }, [dispatch]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await dispatch(refreshAllRequests()).unwrap();
      await dispatch(loadSentRequests()).unwrap();
    } catch {
      /* toast already fired */
    }
    setRefreshing(false);
  }, [dispatch]);

  const handleFilterChange = useCallback(
    (filter: typeof currentFilter) => {
      dispatch(setCurrentFilter(filter));
      const params: BorrowRequestsQuery | undefined =
        filter === 'all'
          ? {}
          : {
              status: filter.toUpperCase() as BorrowRequestsQuery['status'],
            };
      dispatch(loadReceivedRequests(params));
    },
    [dispatch],
  );

  const handleQuickAction = useCallback(
    (request: BorrowRequestDetails, action: 'APPROVE' | 'DENY') => {
      dispatch(setSelectedRequest(request));
      setResponseAction(action);
      setResponseMessage('');
      setResponseModalVisible(true);
    },
    [dispatch],
  );

  const handleSubmitResponse = useCallback(async () => {
    if (!selectedRequest || !responseAction) return;
    const actionData = {
      requestId: selectedRequest.id,
      responseMessage: responseMessage.trim() || undefined,
    };
    try {
      if (responseAction === 'APPROVE') {
        const result = await dispatch(
          approveBorrowRequest(actionData),
        ).unwrap();
        // FE-255 — approveBorrowRequest's fulfilled payload is now
        // `{ response, groupId, itemId, borrowerUserId }`; the BE action
        // response (with `share_created`) lives under `.response`.
        Toast.show({
          type: 'success',
          text1: 'Request approved',
          text2: result.response.share_created
            ? 'Sharing link created; requester notified.'
            : 'Requester has been notified.',
        });
      } else {
        await dispatch(denyBorrowRequest(actionData)).unwrap();
        Toast.show({
          type: 'success',
          text1: 'Request denied',
          text2: 'Requester has been notified.',
        });
      }
      setResponseModalVisible(false);
      setResponseAction(null);
      setResponseMessage('');
      dispatch(refreshAllRequests());
    } catch {
      /* management error toast fires via useEffect */
    }
  }, [dispatch, selectedRequest, responseAction, responseMessage]);

  const statSummary = useMemo(
    () => [
      {
        icon: 'hourglass-empty' as const,
        label: 'Pending',
        value: String(pendingCount),
        tone: fpColors.tertiary,
      },
      {
        icon: 'mail-outline' as const,
        label: 'Total',
        value: String(totalReceived),
        tone: fpColors.primary,
      },
      {
        icon: 'schedule' as const,
        label: 'Expiring',
        value: String(expiringSoon.length),
        tone: fpColors.error,
      },
    ],
    [pendingCount, totalReceived, expiringSoon.length],
  );

  /* ——— Loading / empty / error gates ——— */

  if (
    loading.received &&
    activeTab === 'received' &&
    receivedRequests.length === 0
  ) {
    return (
      <View style={styles.stateWrap}>
        <ActivityIndicator color={fpColors.primary} />
        <Text style={styles.stateText}>Loading requests…</Text>
      </View>
    );
  }

  if (error && receivedRequests.length === 0) {
    return (
      <View style={styles.stateWrap}>
        <MaterialIcons
          name="error-outline"
          size={32}
          color={fpColors.error}
        />
        <Text style={styles.stateTitle}>Couldn’t load requests</Text>
        <Text style={styles.stateText}>{String(error)}</Text>
        <Pressable
          onPress={handleRetry}
          style={({ pressed }) => [
            styles.retryBtn,
            pressed && { opacity: 0.85 },
          ]}
        >
          <MaterialIcons name="refresh" size={16} color={fpColors.onPrimary} />
          <Text style={styles.retryLabel}>Try again</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.page}>
      {/* Summary bento */}
      <View style={styles.statsRow}>
        {statSummary.map((s) => (
          <View key={s.label} style={styles.statCard}>
            <View
              style={[styles.statIconWrap, { backgroundColor: `${s.tone}1a` }]}
            >
              <MaterialIcons name={s.icon} size={16} color={s.tone} />
            </View>
            <Text style={styles.statValue}>{s.value}</Text>
            <Text style={styles.statLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      {/* Received / Sent segmented */}
      <View style={styles.segment}>
        <Pressable
          onPress={() => dispatch(setActiveTab('received'))}
          style={({ pressed }) => [
            styles.segmentBtn,
            activeTab === 'received' && styles.segmentBtnActive,
            pressed && { opacity: 0.9 },
          ]}
        >
          <MaterialIcons
            name="inbox"
            size={16}
            color={
              activeTab === 'received'
                ? fpColors.onPrimary
                : fpColors.onSurface
            }
          />
          <Text
            style={[
              styles.segmentLabel,
              activeTab === 'received' && styles.segmentLabelActive,
            ]}
          >
            Received
          </Text>
          {pendingCount > 0 && activeTab !== 'received' ? (
            <View style={styles.segmentBadge}>
              <Text style={styles.segmentBadgeText}>{pendingCount}</Text>
            </View>
          ) : null}
        </Pressable>
        <Pressable
          onPress={() => dispatch(setActiveTab('sent'))}
          style={({ pressed }) => [
            styles.segmentBtn,
            activeTab === 'sent' && styles.segmentBtnActive,
            pressed && { opacity: 0.9 },
          ]}
        >
          <MaterialIcons
            name="send"
            size={16}
            color={
              activeTab === 'sent' ? fpColors.onPrimary : fpColors.onSurface
            }
          />
          <Text
            style={[
              styles.segmentLabel,
              activeTab === 'sent' && styles.segmentLabelActive,
            ]}
          >
            Sent
          </Text>
        </Pressable>
      </View>

      {/* Status filter chips (received only) */}
      {activeTab === 'received' ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chipRow}
        >
          <FilterChip
            label="All"
            active={currentFilter === 'all'}
            count={totalReceived}
            onPress={() => handleFilterChange('all')}
          />
          <FilterChip
            label="Pending"
            active={currentFilter === 'pending'}
            count={pendingCount}
            onPress={() => handleFilterChange('pending')}
          />
          <FilterChip
            label="Approved"
            active={currentFilter === 'approved'}
            onPress={() => handleFilterChange('approved')}
          />
          <FilterChip
            label="Denied"
            active={currentFilter === 'denied'}
            onPress={() => handleFilterChange('denied')}
          />
          <FilterChip
            label="Expired"
            active={currentFilter === 'expired'}
            onPress={() => handleFilterChange('expired')}
          />
        </ScrollView>
      ) : null}

      {/* List */}
      {activeTab === 'received' ? (
        <FlatList
          data={filteredRequests}
          renderItem={({ item }) => (
            <RequestCard
              mode="received"
              item={item}
              onApprove={() => handleQuickAction(item, 'APPROVE')}
              onDeny={() => handleQuickAction(item, 'DENY')}
            />
          )}
          keyExtractor={(item) => String(item.id)}
          contentContainerStyle={[
            styles.listContent,
            filteredRequests.length === 0 && { flexGrow: 1 },
          ]}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={fpColors.primary}
            />
          }
          ListEmptyComponent={() => <EmptyState filter={currentFilter} />}
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <FlatList
          data={sentRequests as any[]}
          renderItem={({ item }) => (
            <RequestCard
              mode="sent"
              item={item}
              onCancel={() => dispatch(cancelSentRequest({ requestId: item.id }))}
              onClaim={() => dispatch(claimSentRequest({ requestId: item.id }))}
            />
          )}
          keyExtractor={(item: any) =>
            String(item.id || item.request_id)
          }
          contentContainerStyle={[
            styles.listContent,
            (sentRequests?.length || 0) === 0 && { flexGrow: 1 },
          ]}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={fpColors.primary}
            />
          }
          ListEmptyComponent={() => <EmptyState filter="all" sent />}
          showsVerticalScrollIndicator={false}
        />
      )}

      {/* Response modal */}
      <Modal
        visible={responseModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setResponseModalVisible(false)}
      >
        <View style={styles.scrim}>
          <Pressable
            style={styles.scrimTap}
            onPress={() => setResponseModalVisible(false)}
          />
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          >
            <View style={styles.sheet}>
              <View style={styles.handle} />
              <View style={styles.sheetHeader}>
                <Text style={styles.sheetTitle}>
                  {responseAction === 'APPROVE'
                    ? 'Approve request'
                    : 'Deny request'}
                </Text>
                <Pressable
                  onPress={() => setResponseModalVisible(false)}
                  hitSlop={8}
                  style={styles.sheetClose}
                >
                  <MaterialIcons
                    name="close"
                    size={20}
                    color={fpColors.onSurface}
                  />
                </Pressable>
              </View>
              {selectedRequest ? (
                <View style={styles.sheetItem}>
                  <Text style={styles.sheetItemTitle} numberOfLines={1}>
                    {selectedRequest.item_name}
                  </Text>
                  <Text style={styles.sheetItemMeta}>
                    Request from {selectedRequest.requester_name}
                  </Text>
                </View>
              ) : null}
              <Text style={styles.sheetLabel}>
                Message to requester{' '}
                <Text style={styles.sheetOptional}>(optional)</Text>
              </Text>
              <TextInput
                value={responseMessage}
                onChangeText={setResponseMessage}
                multiline
                maxLength={500}
                placeholder={
                  responseAction === 'APPROVE'
                    ? 'Approved! Details to follow.'
                    : 'Sorry, I need this for the next few weeks.'
                }
                placeholderTextColor={`${fpColors.outline}99`}
                style={styles.sheetInput}
              />
              <Text style={styles.sheetCount}>
                {responseMessage.length}/500
              </Text>
              <View style={styles.sheetActions}>
                <Pressable
                  onPress={() => setResponseModalVisible(false)}
                  style={({ pressed }) => [
                    styles.sheetCancel,
                    pressed && { opacity: 0.85 },
                  ]}
                >
                  <Text style={styles.sheetCancelLabel}>Cancel</Text>
                </Pressable>
                <View style={{ flex: 1 }}>
                  <PrimaryCTA
                    label={
                      loading.managing
                        ? 'Submitting…'
                        : responseAction === 'APPROVE'
                          ? 'Approve'
                          : 'Deny'
                    }
                    onPress={handleSubmitResponse}
                    loading={loading.managing}
                    iconRight={
                      loading.managing
                        ? null
                        : responseAction === 'APPROVE'
                          ? 'check-circle'
                          : 'block'
                    }
                  />
                </View>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    </View>
  );
};

/* ——— presentational pieces ——— */

const FilterChip: React.FC<{
  label: string;
  active: boolean;
  count?: number;
  onPress: () => void;
}> = ({ label, active, count, onPress }) => (
  <Pressable
    onPress={onPress}
    style={[styles.chip, active && styles.chipActive]}
  >
    <Text style={[styles.chipLabel, active && styles.chipLabelActive]}>
      {label}
    </Text>
    {count !== undefined && count > 0 ? (
      <View style={[styles.chipCount, active && styles.chipCountActive]}>
        <Text
          style={[
            styles.chipCountText,
            active && styles.chipCountTextActive,
          ]}
        >
          {count}
        </Text>
      </View>
    ) : null}
  </Pressable>
);

const statusMeta = (status: RequestStatus) => {
  switch (status) {
    case 'PENDING':
      return {
        label: 'Pending',
        tone: fpColors.tertiary,
        bg: `${fpColors.tertiary}1a`,
      };
    case 'APPROVED':
      return {
        label: 'Approved',
        tone: fpColors.primary,
        bg: `${fpColors.primary}1a`,
      };
    case 'DENIED':
      return {
        label: 'Denied',
        tone: fpColors.error,
        bg: `${fpColors.error}14`,
      };
    case 'EXPIRED':
      return {
        label: 'Expired',
        tone: fpColors.outline,
        bg: fpColors.surfaceContainerLow,
      };
    default:
      return {
        label: String(status),
        tone: fpColors.onSurfaceVariant,
        bg: fpColors.surfaceContainerLow,
      };
  }
};

const relTime = (iso: string) => {
  const diffMin = (Date.now() - new Date(iso).getTime()) / 60000;
  if (!Number.isFinite(diffMin)) return '';
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  const days = Math.floor(diffMin / 1440);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const RequestCard: React.FC<{
  mode: 'received' | 'sent';
  item: any;
  onApprove?: () => void;
  onDeny?: () => void;
  onCancel?: () => void;
  onClaim?: () => void;
}> = ({ mode, item, onApprove, onDeny, onCancel, onClaim }) => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const meta = statusMeta(item.status);
  const days = item.days_until_expiry;
  const isPending = item.status === 'PENDING';
  const canManage = mode === 'received' && isPending && days > 0;
  const canClaim = mode === 'sent' && item.status === 'APPROVED' && item.claimable === true;

  // FE — Recall-ack CTA on the borrow card. When the row is a PENDING
  // RECALL the viewer received (owner sent it; holder is the target),
  // the user gets a direct "Acknowledge & return" affordance instead
  // of having to drill into ItemDetail to find the Hand-back button.
  // Calls /inventory/{id}/recall-acknowledge which atomically flips
  // custody back + marks the recall row FULFILLED in one transaction.
  const isReceivedRecall =
    mode === 'received' &&
    isPending &&
    String(item.priority || '').toUpperCase() === 'RECALL';
  const [acknowledging, setAcknowledging] = useState(false);

  const handleAcknowledgeRecall = useCallback(async () => {
    if (acknowledging || typeof item.item_id !== 'number') return;
    setAcknowledging(true);
    try {
      await api.acknowledgeRecall(item.item_id);
      Toast.show({
        type: 'success',
        text1: 'Recall acknowledged',
        text2: `${item.item_name ?? 'Item'} has been returned.`,
      });
      // Refresh both inboxes so this row flips to FULFILLED + the
      // returned-to-owner state is reflected everywhere.
      dispatch(refreshAllRequests());
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Please try again.';
      Toast.show({
        type: 'error',
        text1: "Couldn't acknowledge",
        text2: detail,
      });
      setAcknowledging(false);
    }
  }, [acknowledging, item.item_id, item.item_name, dispatch]);

  // FE-252 bug 1 — the expiry chip is only meaningful while the
  // request is still pending. For non-pending rows (Approved /
  // Denied / Fulfilled / Cancelled / EXPIRED) the colored badge
  // tells the full story; the redundant client-side "Expired" chip
  // was leaking onto historical rows after the 7-day window passed.
  const expiryText =
    isPending && typeof days === 'number'
      ? days <= 0
        ? 'Expired'
        : `${days}d left`
      : '';
  const expiryTone =
    typeof days === 'number' && days <= 1
      ? fpColors.error
      : typeof days === 'number' && days <= 3
        ? fpColors.tertiary
        : fpColors.outline;

  // FE-252 bug 3 — make the thumb + title section tap-to-open. Skip
  // the Pressable wrapper entirely when item_id is null (legacy row
  // or item deleted) so we don't render a tappable card that would
  // navigate to a broken ItemDetail.
  const canTap = typeof item.item_id === 'number';
  const handleCardPress = useCallback(() => {
    if (!canTap) return;
    // FE — Speed fix: pass an optimistic item stub through nav so
    // ItemDetail can paint hero photo + title + description instantly
    // while the single-item GET refreshes the canonical row in the
    // background. The BorrowRequestDetails row already carries
    // item_name / item_image_url / item_description / item_id /
    // user_id (owner) — enough for a non-blank first frame.
    const optimisticItem = {
      id: item.item_id,
      item_name: item.item_name,
      item_description: item.item_description ?? '',
      item_image_url: item.item_image_url ?? null,
      primary_photo_url: item.item_image_url ?? null,
      source_image_url: item.item_image_url ?? null,
      user_id: item.owner_user_id ?? null,
      current_custodian_user_id: item.current_custodian_user_id ?? null,
    };
    navigation.navigate('ItemDetail', {
      itemId: item.item_id,
      primaryGroupId: item.group_id ?? undefined,
      // @ts-ignore — nav params accept arbitrary stub for passedItem
      passedItem: optimisticItem,
    });
  }, [canTap, item, navigation]);

  const topRowContent = (
    <>
      <View style={styles.thumbWrap}>
        <SmartImage
          uri={item.item_image_url}
          style={styles.thumb}
          placeholder="camera-outline"
        />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.title} numberOfLines={1}>
          {item.item_name}
        </Text>
        <Text style={styles.sub} numberOfLines={1}>
          {mode === 'received'
            ? `From ${item.requester_name}`
            : `To ${item.owner_name || 'Owner'}`}
        </Text>
        <Text style={styles.meta}>
          {relTime(item.created_at)}
          {expiryText ? (
            <Text style={{ color: expiryTone }}>{`  ·  ${expiryText}`}</Text>
          ) : null}
        </Text>
      </View>
      <View style={[styles.badge, { backgroundColor: meta.bg }]}>
        <Text style={[styles.badgeText, { color: meta.tone }]}>
          {meta.label}
        </Text>
      </View>
    </>
  );

  return (
    <View style={styles.card}>
      {canTap ? (
        <Pressable
          onPress={handleCardPress}
          accessibilityRole="button"
          accessibilityLabel={`Open ${item.item_name}`}
          testID={`borrow-card-open-${item.id}`}
          style={({ pressed }) => [
            styles.cardTop,
            pressed && { opacity: 0.85 },
          ]}
        >
          {topRowContent}
        </Pressable>
      ) : (
        <View style={styles.cardTop}>{topRowContent}</View>
      )}

      {/* Sprint 4 / FE-10.8 + 10.9 — crew context + priority + waitlist
          chips. Visible on in-crew rows (group_id set) or RECALL / URGENT
          priority rows or queued (#2+) waitlist rows. Marketplace rows
          with default priority + no queue render no chips here.
          FE-228 — mode forwards so the waitlist chip reads "You're #N
          in line" on sent rows vs "Waitlist #N" on received rows. */}
      <CrewContextChips item={item} mode={mode} />

      {item.message ? (
        <View style={styles.messageBox}>
          <MaterialIcons
            name="format-quote"
            size={14}
            color={fpColors.primary}
          />
          <Text style={styles.messageText} numberOfLines={3}>
            {item.message}
          </Text>
        </View>
      ) : null}

      {/* FE-252 bug 2 — surface the owner's decline reason (or
          approval note) on the requester's side. Tinted red for
          DENIED, primary for everything else, so the user sees at a
          glance whether their borrow was rejected. Reuses messageBox
          shape with an inline color override. */}
      {item.response_message ? (
        <View
          style={[
            styles.messageBox,
            {
              borderLeftColor:
                item.status === 'DENIED'
                  ? fpColors.error
                  : fpColors.primary,
              backgroundColor:
                item.status === 'DENIED'
                  ? `${fpColors.error}0a`
                  : `${fpColors.primary}0a`,
            },
          ]}
        >
          <MaterialIcons
            name="format-quote"
            size={14}
            color={
              item.status === 'DENIED' ? fpColors.error : fpColors.primary
            }
          />
          <View style={{ flex: 1 }}>
            <Text style={styles.responseLabel}>
              {mode === 'sent' ? "Owner's response" : 'Your response'}
            </Text>
            <Text style={styles.messageText} numberOfLines={3}>
              {item.response_message}
            </Text>
          </View>
        </View>
      ) : null}

      {/* FE — Received RECALL has its own action: Acknowledge & return.
          Owner doesn't need to approve/deny their own recall — the
          custodian (viewer here) just needs to confirm the return.
          Calls /inventory/{id}/recall-acknowledge which flips custody
          and marks the recall FULFILLED. */}
      {isReceivedRecall ? (
        <View style={styles.actions}>
          <Pressable
            onPress={handleAcknowledgeRecall}
            disabled={acknowledging || typeof item.item_id !== 'number'}
            style={({ pressed }) => [
              styles.actionBtn,
              styles.actionRecallAck,
              (pressed || acknowledging) && { opacity: 0.85 },
              acknowledging && { opacity: 0.6 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Acknowledge recall and return item"
            testID={`borrow-card-recall-ack-${item.id}`}
          >
            <MaterialIcons
              name="priority-high"
              size={16}
              color={(fpColors as any).onError ?? '#FFFFFF'}
            />
            <Text
              style={[
                styles.actionLabel,
                { color: (fpColors as any).onError ?? '#FFFFFF' },
              ]}
            >
              {acknowledging ? 'Returning…' : 'Acknowledge & return'}
            </Text>
          </Pressable>
        </View>
      ) : canManage ? (
        <View style={styles.actions}>
          <Pressable
            onPress={onDeny}
            style={({ pressed }) => [
              styles.actionBtn,
              styles.actionDeny,
              pressed && { opacity: 0.9 },
            ]}
          >
            <MaterialIcons name="close" size={16} color={fpColors.error} />
            <Text style={[styles.actionLabel, { color: fpColors.error }]}>
              Deny
            </Text>
          </Pressable>
          <Pressable
            onPress={onApprove}
            style={({ pressed }) => [
              styles.actionBtn,
              styles.actionApprove,
              pressed && { opacity: 0.9 },
            ]}
          >
            <MaterialIcons
              name="check"
              size={16}
              color={fpColors.onPrimary}
            />
            <Text style={[styles.actionLabel, { color: fpColors.onPrimary }]}>
              Approve
            </Text>
          </Pressable>
        </View>
      ) : null}

      {mode === 'sent' && (isPending || canClaim) ? (
        <View style={styles.actions}>
          {isPending ? (
            <Pressable
              onPress={onCancel}
              style={({ pressed }) => [
                styles.actionBtn,
                styles.actionDeny,
                pressed && { opacity: 0.9 },
              ]}
            >
              <MaterialIcons name="close" size={16} color={fpColors.error} />
              <Text style={[styles.actionLabel, { color: fpColors.error }]}>
                Cancel
              </Text>
            </Pressable>
          ) : null}
          {canClaim ? (
            <Pressable
              onPress={onClaim}
              style={({ pressed }) => [
                styles.actionBtn,
                styles.actionApprove,
                pressed && { opacity: 0.9 },
              ]}
            >
              <MaterialIcons
                name="check-circle"
                size={16}
                color={fpColors.onPrimary}
              />
              <Text
                style={[styles.actionLabel, { color: fpColors.onPrimary }]}
              >
                Claim now
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const EmptyState: React.FC<{
  filter: 'all' | 'pending' | 'approved' | 'denied' | 'expired';
  sent?: boolean;
}> = ({ filter, sent }) => (
  <View style={styles.empty}>
    <MaterialIcons
      name={sent ? 'outbox' : 'mail-outline'}
      size={36}
      color={fpColors.outline}
    />
    <Text style={styles.emptyTitle}>
      {sent
        ? 'No sent requests yet'
        : filter === 'pending'
          ? 'No pending requests'
          : 'No requests yet'}
    </Text>
    <Text style={styles.emptyMeta}>
      {sent
        ? 'Requests you send from public items will appear here.'
        : filter === 'pending'
          ? "You're all caught up."
          : 'Incoming borrow requests will show up here.'}
    </Text>
  </View>
);

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },

  stateWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 10,
  },
  stateTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  stateText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    maxWidth: 320,
  },
  retryBtn: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: fpColors.primary,
  },
  retryLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimary,
  },

  statsRow: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 14,
  },
  statCard: {
    flex: 1,
    borderRadius: 14,
    padding: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 6,
  },
  statIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statValue: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 20,
    color: fpColors.onSurface,
    letterSpacing: -0.3,
  },
  statLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  segment: {
    flexDirection: 'row',
    gap: 8,
    padding: 4,
    marginHorizontal: 16,
    marginTop: 14,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  segmentBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderRadius: 9,
  },
  segmentBtnActive: { backgroundColor: fpColors.primary },
  segmentLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  segmentLabelActive: { color: fpColors.onPrimary },
  segmentBadge: {
    minWidth: 20,
    height: 18,
    borderRadius: 9,
    backgroundColor: fpColors.tertiary,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  segmentBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.onPrimary,
  },

  chipRow: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  chipActive: {
    backgroundColor: fpColors.primary,
    borderColor: fpColors.primary,
  },
  chipLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  chipLabelActive: { color: fpColors.onPrimary },
  chipCount: {
    minWidth: 18,
    paddingHorizontal: 5,
    height: 18,
    borderRadius: 9,
    backgroundColor: `${fpColors.primary}22`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chipCountActive: { backgroundColor: 'rgba(255,255,255,0.25)' },
  chipCountText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.primary,
  },
  chipCountTextActive: { color: fpColors.onPrimary },

  listContent: { padding: 16, gap: 12, paddingBottom: 40 },

  card: {
    padding: 14,
    borderRadius: 16,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 10,
  },
  cardTop: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  thumbWrap: {
    width: 56,
    height: 56,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  thumb: { width: '100%', height: '100%' },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  sub: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 1,
  },
  meta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.outline,
    marginTop: 2,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  badgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },

  messageBox: {
    flexDirection: 'row',
    gap: 8,
    padding: 10,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}0a`,
    borderLeftWidth: 3,
    borderLeftColor: fpColors.primary,
  },
  messageText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurface,
    fontStyle: 'italic',
  },
  responseLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 10,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: fpColors.outline,
    marginBottom: 2,
  },

  actions: { flexDirection: 'row', gap: 10 },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
  actionApprove: {
    backgroundColor: fpColors.primary,
    borderColor: fpColors.primary,
  },
  actionDeny: {
    backgroundColor: `${fpColors.error}10`,
    borderColor: `${fpColors.error}33`,
  },
  // FE — Recall-ack CTA: full red so it reads as urgent. The user
  // is being asked to return an item the owner needs back.
  actionRecallAck: {
    backgroundColor: fpColors.error,
    borderColor: fpColors.error,
  },
  actionLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
  },

  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 6,
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
    marginTop: 4,
  },
  emptyMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    maxWidth: 300,
  },

  /* ——— sheet ——— */
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(31,26,34,0.45)',
    justifyContent: 'flex-end',
  },
  scrimTap: { flex: 1 },
  sheet: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
    gap: 12,
  },
  handle: {
    alignSelf: 'center',
    width: 44,
    height: 4,
    borderRadius: 2,
    backgroundColor: fpColors.outlineVariant,
    marginTop: 4,
    marginBottom: 4,
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sheetTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  sheetClose: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sheetItem: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  sheetItemTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  sheetItemMeta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  sheetLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  sheetOptional: {
    fontFamily: 'Inter_400Regular',
    color: fpColors.onSurfaceVariant,
  },
  sheetInput: {
    minHeight: 90,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    padding: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    textAlignVertical: 'top',
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  sheetCount: {
    alignSelf: 'flex-end',
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.outline,
  },
  sheetActions: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'stretch',
  },
  sheetCancel: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sheetCancelLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
});
